import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Message } from 'semantic-ui-react'
import { LoaderBar } from 'components'
import { AppPrimaryButton, AppSecondaryButton, CustomDropdown } from 'elements'
import { useAuth } from 'context'
import { GetTeamMemberList } from 'hooks/user'
import { useNotification } from 'hooks/toast'
import {
    ActionPlanRuleService,
    CustomerService,
    EscalationPolicyService,
    GroupService,
    MasterAnswersService,
    SettingsTabs,
    TemplateService,
    UsersService,
} from 'utility'
import { TemplateUserService } from 'utility/services/template-user'
import {
    PageWrap,
    PageContent,
    Breadcrumb,
    PageTitle,
    PageSubtitle,
    WizardStepper,
    StepPip,
    WizardPanel,
    WhyBox,
    WizardFooter,
    PreviewCard,
    UrgencyGrid,
    UrgCard,
    RadioGroup,
    RadioCard,
    CustomerSearch,
    CustomerScopeList,
    CustomerScopeToolbar,
    CustomerScopeRows,
    CustomerScopeRow,
    CustomerChipRow,
    CustomerChip,
    CustomerSelectedCount,
    PillCustomers,
    VendorDropdownStyles,
} from './styles'
import {
    EscalationPolicyFormModal,
    CREATE_NEW_POLICY_KEY,
    formatPolicyDropdownLabel,
    formatPolicyHint,
    enrichPoliciesWithRecipientNames,
    normalizeEscalationPolicyList,
    PolicyFieldBox,
    PolicyHint,
    LinkBtn,
} from 'page-components/escalation-policy'
import {
    ActionPlanCategoryFormModal,
    CREATE_NEW_CATEGORY_KEY,
    isActionPlanCategoryAdmin,
} from 'page-components/action-plan-category'
import {
    AddVendorCompanyForm,
    AddVendorContactForm,
    EditVendorCompanyForm,
    EditVendorContactForm,
    VendorManageBar,
} from './VendorInlineForms'
import {
    assigneePanelKeyFromType,
    alignTriggerValuesToQuestion,
    buildQuestionDropdownOptions,
    resolveAssigneeTypes,
    DEFAULT_PRIORITY_SLA,
    clampSlaMinutes,
    formatCustomerSummary,
    formatMinutesLabel,
    getSlaMaxMinutesForPriority,
    mergeTemplateUsersForPicker,
    FALLBACK_PRIORITIES,
    buildRulePayload,
    enrichQuestionsWithMasterAnswers,
    extractTemplateQuestions,
    filterQuestionDropdownOptions,
    formatUserPickerLabel,
    getAnswerOptionPresentation,
    getApiErrorMessage,
    getAssigneeOptionUi,
    getPriorityLevel,
    getQuestionAnswerOptions,
    isTriggerValueSelected,
    MAX_ASSIGNEES,
    MAX_TRIGGER_ANSWERS,
    normalizeActionPlanTeamList,
    normalizeCategoryList,
    normalizeCompanyCustomers,
    normalizePriorityList,
    normalizeRoleList,
    normalizeRule,
    normalizeTeamGroupList,
    normalizeTriggerOperatorList,
    normalizeVendorCompanyList,
    normalizeVendorContactList,
    resolveTemplateNumericId,
    toDropdownOptions,
    ADD_VENDOR_COMPANY_KEY,
    ADD_VENDOR_CONTACT_KEY,
    withAddVendorCompanyOption,
    withAddVendorContactOption,
} from './utils'

const STEP_LABELS = [
    'Pick the question',
    'Choose the trigger answer',
    'Which customers',
    'Who gets the ticket',
    'How urgent',
    'Review & save',
]

const URGENCY_ICONS = {
    low: '⬇',
    medium: '●',
    high: '⬆',
    critical: '🚨',
}

const emptyForm = () => ({
    auditQuestionId: null,
    triggerValue: '',
    triggerValues: [],
    triggerOperatorId: 1,
    fixedAssignedToTypeId: 1,
    fixedAssignedToId: null,
    vendorCompanyId: null,
    assignees: [{ typeId: 1, assignedToId: null, vendorCompanyId: null }],
    priorityId: null,
    ackSlaMinutes: 240,
    resolveSlaMinutes: 2880,
    categoryId: null,
    ticketType: 'General',
    isEnabled: true,
    escalationPolicyId: null,
    customerMode: 'all',
    customerIds: [],
})

const syncLegacyAssigneeFields = assignees => {
    const primary = assignees[0] || {}
    return {
        assignees,
        fixedAssignedToTypeId: primary.typeId ?? 1,
        fixedAssignedToId: primary.assignedToId ?? null,
        vendorCompanyId: primary.vendorCompanyId ?? null,
    }
}

export const RuleWizard = ({ templateId, ruleId, templateName: nameProp }) => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data: allTeamMembers } = GetTeamMemberList()
    const { successToast, errorToast, warningToast } = useNotification()
    const toastRef = useRef({ successToast, errorToast, warningToast })
    toastRef.current = { successToast, errorToast, warningToast }
    const isEdit = Boolean(ruleId)

    const [step, setStep] = useState(1)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [templateName, setTemplateName] = useState(nameProp || '')
    const [questions, setQuestions] = useState([])
    const [form, setForm] = useState(emptyForm())
    const [priorities, setPriorities] = useState([])
    const [categories, setCategories] = useState([])
    const [assignedToTypes, setAssignedToTypes] = useState([])
    const [templateUsers, setTemplateUsers] = useState([])
    const [templateUsersRaw, setTemplateUsersRaw] = useState(null)
    const [teams, setTeams] = useState([])
    const [roles, setRoles] = useState([])
    const [vendorCompanies, setVendorCompanies] = useState([])
    const [vendorContacts, setVendorContacts] = useState([])
    const [escalationPolicies, setEscalationPolicies] = useState([])
    const [policyModalOpen, setPolicyModalOpen] = useState(false)
    const [editingPolicy, setEditingPolicy] = useState(null)
    const [categoryModalOpen, setCategoryModalOpen] = useState(false)
    const [editingCategory, setEditingCategory] = useState(null)
    const [numericTemplateId, setNumericTemplateId] = useState(null)
    const [showAddVendorCompanyForm, setShowAddVendorCompanyForm] =
        useState(false)
    const [showAddVendorContactForm, setShowAddVendorContactForm] =
        useState(false)
    const [editVendorCompany, setEditVendorCompany] = useState(null)
    const [editVendorContact, setEditVendorContact] = useState(null)
    const [confirmDeleteVendorCompany, setConfirmDeleteVendorCompany] =
        useState(false)
    const [confirmDeleteVendorContact, setConfirmDeleteVendorContact] =
        useState(false)
    const [deleteVendorCompanyWarning, setDeleteVendorCompanyWarning] =
        useState('')
    const [deletingVendorCompany, setDeletingVendorCompany] = useState(false)
    const [deletingVendorContact, setDeletingVendorContact] = useState(false)
    const [companyCustomers, setCompanyCustomers] = useState([])
    const [customersLoadError, setCustomersLoadError] = useState('')
    const [customerSearch, setCustomerSearch] = useState('')
    const [customerSaveError, setCustomerSaveError] = useState('')
    const [savedCustomerNames, setSavedCustomerNames] = useState({})

    const companyId =
        user?.Companies?.[0]?.CompanyId ?? user?.CompanyId ?? null

    const managePath = `/app/templates/${templateId}/auto-rules`

    const loadData = useCallback(async () => {
        setLoading(true)
        try {
            const template = await TemplateService.getTemplateDetails(
                templateId,
            )
            const numericId = resolveTemplateNumericId(template)
            if (!numericId) {
                toastRef.current.errorToast('Could not resolve template ID.')
                return
            }
            setNumericTemplateId(numericId)

            const usersPromise = Promise.allSettled([
                TemplateUserService.getByTemplate(numericId),
                companyId
                    ? TemplateUserService.getList(companyId, numericId)
                    : Promise.resolve([]),
                UsersService.getTeamUsers({ PageSize: 5000000 }),
            ]).then(results => {
                const teamRes =
                    results[2].status === 'fulfilled' ? results[2].value : []
                const teamMembers = Array.isArray(teamRes)
                    ? teamRes
                    : teamRes?.Items || teamRes?.Data || []
                return {
                    byTemplate:
                        results[0].status === 'fulfilled'
                            ? results[0].value
                            : [],
                    byList:
                        results[1].status === 'fulfilled'
                            ? results[1].value
                            : [],
                    teamMembers,
                }
            })

            const [
                priResult,
                catResult,
                typesResult,
                usersResult,
                actionPlanTeamsResult,
                teamGroupResult,
                rolesResult,
                vendorsResult,
                operatorsResult,
                policiesResult,
                customersResult,
            ] = await Promise.allSettled([
                ActionPlanRuleService.getPriorities(),
                ActionPlanRuleService.getCategories(),
                ActionPlanRuleService.getAssignedToTypes(),
                usersPromise,
                ActionPlanRuleService.getTeams(),
                GroupService.list({ PageNumber: 1, PageSize: 500 }),
                GroupService.getRoleTypes(),
                ActionPlanRuleService.getVendorCompanies(),
                ActionPlanRuleService.getTriggerOperators(),
                EscalationPolicyService.list(),
                CustomerService.list({
                    CallFrom: 1,
                    PageNumber: 1,
                    PageSize: 5000000,
                }),
            ])

            const pri =
                priResult.status === 'fulfilled' ? priResult.value : null
            const cat =
                catResult.status === 'fulfilled' ? catResult.value : null
            const types =
                typesResult.status === 'fulfilled' ? typesResult.value : []
            const actionPlanTeams =
                actionPlanTeamsResult.status === 'fulfilled'
                    ? normalizeActionPlanTeamList(actionPlanTeamsResult.value)
                    : []
            const teamGroupTeams =
                teamGroupResult.status === 'fulfilled'
                    ? normalizeTeamGroupList(teamGroupResult.value)
                    : []
            const rolesRes =
                rolesResult.status === 'fulfilled' ? rolesResult.value : []
            const vendorsRes =
                vendorsResult.status === 'fulfilled'
                    ? vendorsResult.value
                    : []
            const operatorsRes =
                operatorsResult.status === 'fulfilled'
                    ? operatorsResult.value
                    : []
            const usersPayload =
                usersResult.status === 'fulfilled' ? usersResult.value : null
            const resolvedTeams = actionPlanTeams.length
                ? actionPlanTeams
                : teamGroupTeams
            const resolvedVendors = normalizeVendorCompanyList(vendorsRes)
            setEscalationPolicies(
                enrichPoliciesWithRecipientNames(
                    normalizeEscalationPolicyList(
                        policiesResult.status === 'fulfilled'
                            ? policiesResult.value
                            : [],
                    ),
                    {
                        teamMembers: usersPayload?.teamMembers || [],
                        teams: resolvedTeams,
                        vendors: resolvedVendors,
                    },
                ),
            )

            const lookupFailed =
                priResult.status === 'rejected' ||
                catResult.status === 'rejected' ||
                typesResult.status === 'rejected'
            if (lookupFailed) {
                toastRef.current.warningToast(
                    'Some lookups failed — using defaults where needed.',
                )
            }

            const priorityList = pri
                ? normalizePriorityList(pri)
                : FALLBACK_PRIORITIES
            const categoryList = cat ? normalizeCategoryList(cat) : []
            const typeList = resolveAssigneeTypes(types)

            setTemplateName(
                template?.TemplateName || nameProp || 'Template',
            )
            const rawQuestions = extractTemplateQuestions(template)
            const enrichedQuestions = await enrichQuestionsWithMasterAnswers(
                rawQuestions,
                id => MasterAnswersService.getItems(id),
            )
            setQuestions(enrichedQuestions)
            setPriorities(priorityList)
            setCategories(categoryList)
            setAssignedToTypes(typeList)
            setTemplateUsersRaw(usersPayload)
            setTeams(resolvedTeams)
            setRoles(normalizeRoleList(rolesRes))
            setVendorCompanies(resolvedVendors)
            if (customersResult.status === 'fulfilled') {
                setCompanyCustomers(
                    normalizeCompanyCustomers(customersResult.value),
                )
                setCustomersLoadError('')
            } else {
                setCompanyCustomers([])
                setCustomersLoadError(
                    getApiErrorMessage(customersResult.reason) ||
                        'Could not load customers.',
                )
            }

            const operatorList =
                normalizeTriggerOperatorList(operatorsRes)
            const equalsOp =
                operatorList.find(op =>
                    String(op.name).toLowerCase().includes('equal'),
                ) || { id: 1 }

            if (isEdit) {
                try {
                    const rulesRes =
                        await ActionPlanRuleService.getRulesByTemplate(
                            numericId,
                        )
                    const rules = (
                        Array.isArray(rulesRes)
                            ? rulesRes
                            : rulesRes?.Items ||
                              rulesRes?.Data ||
                              rulesRes?.Rules ||
                              []
                    ).map(normalizeRule)
                    const existing = rules.find(
                        r => String(r.id) === String(ruleId),
                    )
                    if (existing) {
                        const editQuestion = enrichedQuestions.find(
                            q =>
                                Number(q.auditQuestionId) ===
                                Number(existing.auditQuestionId),
                        )
                        let triggerValues = []
                        if (existing.triggerValues?.length) {
                            triggerValues = existing.triggerValues
                        } else if (existing.triggerValue) {
                            triggerValues = [existing.triggerValue]
                        }
                        triggerValues = alignTriggerValuesToQuestion(
                            editQuestion,
                            triggerValues,
                        )
                        const assignees =
                            existing.assignees?.length
                                ? existing.assignees
                                : [
                                      {
                                          typeId:
                                              existing.fixedAssignedToTypeId ||
                                              1,
                                          assignedToId:
                                              existing.fixedAssignedToId,
                                          vendorCompanyId: null,
                                      },
                                  ]
                        setForm({
                            auditQuestionId: existing.auditQuestionId,
                            triggerValue: triggerValues[0] || '',
                            triggerValues,
                            triggerOperatorId:
                                existing.triggerOperatorId ||
                                equalsOp.id ||
                                1,
                            ...syncLegacyAssigneeFields(assignees),
                            priorityId: existing.priorityId,
                            ackSlaMinutes: existing.ackSlaMinutes || 240,
                            resolveSlaMinutes:
                                existing.resolveSlaMinutes || 2880,
                            categoryId: existing.categoryId,
                            ticketType: existing.ticketType || 'General',
                            isEnabled: existing.isEnabled,
                            escalationPolicyId:
                                existing.escalationPolicyId || null,
                            customerMode: existing.appliesToAllCustomers
                                ? 'all'
                                : 'selected',
                            customerIds: existing.customerIds || [],
                        })
                        const nameMap = {}
                        ;(existing.customers || []).forEach(c => {
                            if (c?.id != null && c.name) {
                                nameMap[c.id] = c.name
                            }
                        })
                        setSavedCustomerNames(nameMap)
                    }
                } catch {
                    // rules list optional until backend is live
                }
            } else {
                const med = priorityList.find(p =>
                    String(p.name).toLowerCase().includes('medium'),
                )
                const defaultPri = med || priorityList[0]
                if (defaultPri) {
                    const level = getPriorityLevel(defaultPri.name)
                    const sla =
                        DEFAULT_PRIORITY_SLA[level] ||
                        DEFAULT_PRIORITY_SLA.medium
                    const defaultCategory = categoryList[0]
                    setForm(f => ({
                        ...f,
                        priorityId: defaultPri.id,
                        triggerOperatorId: equalsOp.id || 1,
                        ackSlaMinutes: sla.ack,
                        resolveSlaMinutes: sla.resolve,
                        ...(categoryList.length === 1 && defaultCategory
                            ? {
                                  categoryId: defaultCategory.id,
                                  ticketType:
                                      defaultCategory.name || f.ticketType,
                              }
                            : {}),
                    }))
                } else {
                    setForm(f => ({
                        ...f,
                        triggerOperatorId: equalsOp.id || 1,
                    }))
                }
                if (typeList.length) {
                    setForm(f => ({
                        ...f,
                        ...syncLegacyAssigneeFields([
                            {
                                typeId: typeList[0].id,
                                assignedToId: null,
                                vendorCompanyId: null,
                            },
                        ]),
                    }))
                }
            }
        } catch (err) {
            toastRef.current.errorToast(getApiErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }, [templateId, ruleId, isEdit, nameProp, companyId])

    useEffect(() => {
        if (!templateUsersRaw) return
        const teamPool =
            allTeamMembers?.length > 0
                ? allTeamMembers
                : templateUsersRaw.teamMembers || []
        setTemplateUsers(
            mergeTemplateUsersForPicker(
                templateUsersRaw.byTemplate,
                templateUsersRaw.byList,
                teamPool,
            ),
        )
    }, [templateUsersRaw, allTeamMembers])

    useEffect(() => {
        loadData()
    }, [loadData])

    const getPanelKeyForTypeId = useCallback(
        typeId => {
            const typeRow = assignedToTypes.find(
                t => Number(t.id) === Number(typeId),
            )
            return assigneePanelKeyFromType(typeRow) || 'user'
        },
        [assignedToTypes],
    )

    const vendorContactEntry = useMemo(
        () =>
            (form.assignees || []).find(
                a => getPanelKeyForTypeId(a.typeId) === 'vendor-contact',
            ),
        [form.assignees, getPanelKeyForTypeId],
    )

    useEffect(() => {
        const companyIdForContacts = vendorContactEntry?.vendorCompanyId
        if (!companyIdForContacts) {
            setVendorContacts([])
            return undefined
        }
        let cancelled = false
        ActionPlanRuleService.getVendorContacts(companyIdForContacts)
            .then(res => {
                if (!cancelled) {
                    setVendorContacts(normalizeVendorContactList(res))
                }
            })
            .catch(() => {
                if (!cancelled) setVendorContacts([])
            })
        return () => {
            cancelled = true
        }
    }, [vendorContactEntry?.vendorCompanyId])

    const assigneeOptions = useMemo(
        () =>
            assignedToTypes.map(t => {
                const ui = getAssigneeOptionUi(t)
                return {
                    typeId: t.id,
                    panelKey: ui.panelKey,
                    title: ui.title,
                    desc: ui.desc,
                }
            }),
        [assignedToTypes],
    )

    const selectedQuestion = useMemo(
        () =>
            questions.find(
                q => Number(q.auditQuestionId) === Number(form.auditQuestionId),
            ),
        [questions, form.auditQuestionId],
    )

    const answerOptions = useMemo(
        () => getQuestionAnswerOptions(selectedQuestion),
        [selectedQuestion],
    )

    const questionDropdownOptions = useMemo(
        () => buildQuestionDropdownOptions(questions),
        [questions],
    )

    const userDropdownOptions = useMemo(
        () =>
            toDropdownOptions(templateUsers, {
                getText: formatUserPickerLabel,
            }),
        [templateUsers],
    )

    const roleDropdownOptions = useMemo(
        () => toDropdownOptions(roles),
        [roles],
    )

    const teamDropdownOptions = useMemo(
        () => toDropdownOptions(teams),
        [teams],
    )

    const vendorCompanyDropdownOptions = useMemo(
        () =>
            withAddVendorCompanyOption(toDropdownOptions(vendorCompanies)),
        [vendorCompanies],
    )

    const vendorContactDropdownOptions = useMemo(
        () =>
            withAddVendorContactOption(
                toDropdownOptions(vendorContacts, {
                    getDescription: c => c.email || '',
                }),
            ),
        [vendorContacts],
    )

    const selectedPolicy = useMemo(
        () =>
            escalationPolicies.find(
                p => Number(p.id) === Number(form.escalationPolicyId),
            ),
        [escalationPolicies, form.escalationPolicyId],
    )

    const policyDropdownOptions = useMemo(() => {
        const active = escalationPolicies.filter(p => p.isActive)
        const selected = selectedPolicy
        const list = selected && !selected.isActive ? [selected, ...active] : active
        return [
            {
                key: 'none',
                value: '',
                text: '— None (no escalation) —',
            },
            ...list.map(p => ({
                key: String(p.id),
                value: p.id,
                text: formatPolicyDropdownLabel(p),
            })),
            {
                key: CREATE_NEW_POLICY_KEY,
                value: CREATE_NEW_POLICY_KEY,
                text: '＋ Create new policy…',
            },
        ]
    }, [escalationPolicies, selectedPolicy])

    const canManageCategories = isActionPlanCategoryAdmin(user)

    const categoryDropdownOptions = useMemo(() => {
        const opts = toDropdownOptions(categories)
        if (canManageCategories) {
            opts.push({
                key: CREATE_NEW_CATEGORY_KEY,
                value: CREATE_NEW_CATEGORY_KEY,
                text: '＋ Add new category…',
            })
        }
        return opts
    }, [categories, canManageCategories])

    const selectedPriority = priorities.find(p => p.id === form.priorityId)
    const selectedCategory = categories.find(c => c.id === form.categoryId)

    const customerNameById = useMemo(() => {
        const map = { ...savedCustomerNames }
        companyCustomers.forEach(c => {
            map[c.id] = c.name
        })
        return map
    }, [companyCustomers, savedCustomerNames])

    const pickerCustomers = useMemo(() => {
        const rows = companyCustomers.map(c => ({ ...c, missing: false }))
        const known = new Set(rows.map(c => c.id))
        ;(form.customerIds || []).forEach(rawId => {
            const id = Number(rawId)
            if (!Number.isFinite(id) || known.has(id)) return
            rows.unshift({
                id,
                name: customerNameById[id] || `Customer #${id}`,
                missing: true,
            })
            known.add(id)
        })
        return rows
    }, [companyCustomers, form.customerIds, customerNameById])

    const filteredCustomers = useMemo(() => {
        const q = customerSearch.trim().toLowerCase()
        if (!q) return pickerCustomers
        return pickerCustomers.filter(
            c =>
                c.name.toLowerCase().includes(q) ||
                String(c.id).includes(q),
        )
    }, [pickerCustomers, customerSearch])

    const selectedCustomerNames = useMemo(
        () =>
            (form.customerIds || []).map(
                id => customerNameById[Number(id)] || `Customer #${id}`,
            ),
        [form.customerIds, customerNameById],
    )

    const customerSummaryLabel =
        form.customerMode === 'selected'
            ? formatCustomerSummary(selectedCustomerNames, {
                  allLabel: 'No customers selected',
              })
            : 'All customers'

    const customerEmptyMessage = (() => {
        if (customersLoadError) return customersLoadError
        if (customerSearch.trim()) return 'No customers match your search.'
        return 'No customers found for your company.'
    })()

    const customerCountLabel = (() => {
        const total = pickerCustomers.length
        const noun = total === 1 ? 'customer' : 'customers'
        if (customerSearch.trim()) {
            return `${filteredCustomers.length} of ${total} customers`
        }
        return `${total} ${noun}`
    })()

    const allVisibleCustomersSelected =
        filteredCustomers.length > 0 &&
        filteredCustomers.every(c =>
            (form.customerIds || []).some(id => Number(id) === c.id),
        )

    const setCustomerMode = mode => {
        setCustomerSaveError('')
        setForm(prev => ({ ...prev, customerMode: mode }))
    }

    const toggleCustomerId = id => {
        setCustomerSaveError('')
        setForm(prev => {
            const ids = prev.customerIds || []
            const has = ids.some(x => Number(x) === Number(id))
            return {
                ...prev,
                customerMode: 'selected',
                customerIds: has
                    ? ids.filter(x => Number(x) !== Number(id))
                    : [...ids, Number(id)],
            }
        })
    }

    const toggleVisibleCustomers = () => {
        setCustomerSaveError('')
        setForm(prev => {
            const visibleIds = filteredCustomers.map(c => c.id)
            const selected = new Set((prev.customerIds || []).map(Number))
            const allOn =
                visibleIds.length > 0 && visibleIds.every(id => selected.has(id))
            if (allOn) {
                const drop = new Set(visibleIds)
                return {
                    ...prev,
                    customerMode: 'selected',
                    customerIds: (prev.customerIds || []).filter(
                        id => !drop.has(Number(id)),
                    ),
                }
            }
            visibleIds.forEach(id => selected.add(id))
            return {
                ...prev,
                customerMode: 'selected',
                customerIds: [...selected],
            }
        })
    }

    const findEntityLabel = (panelKey, assignedToId, vendorCompanyId) => {
        if (assignedToId == null || assignedToId === '') return null
        if (panelKey === 'user') {
            const u = templateUsers.find(
                x => Number(x.id) === Number(assignedToId),
            )
            return u ? formatUserPickerLabel(u) : null
        }
        if (panelKey === 'team') {
            return (
                teams.find(t => Number(t.id) === Number(assignedToId))?.name ||
                null
            )
        }
        if (panelKey === 'role') {
            return (
                roles.find(r => Number(r.id) === Number(assignedToId))?.name ||
                null
            )
        }
        if (panelKey === 'vendor-company') {
            return (
                vendorCompanies.find(
                    c => Number(c.id) === Number(assignedToId),
                )?.name || null
            )
        }
        if (panelKey === 'vendor-contact') {
            const contact = vendorContacts.find(
                c => Number(c.id) === Number(assignedToId),
            )
            if (!contact) return null
            const company = vendorCompanies.find(
                c => Number(c.id) === Number(vendorCompanyId),
            )?.name
            return company ? `${contact.name} (${company})` : contact.name
        }
        return null
    }

    const assigneeLabel = useMemo(() => {
        const labels = (form.assignees || [])
            .map(a => {
                const panelKey = getPanelKeyForTypeId(a.typeId)
                const opt = assigneeOptions.find(
                    o => Number(o.typeId) === Number(a.typeId),
                )
                const name = findEntityLabel(
                    panelKey,
                    a.assignedToId,
                    a.vendorCompanyId,
                )
                if (!name) return null
                return opt?.title ? `${opt.title}: ${name}` : name
            })
            .filter(Boolean)
        return labels.length ? labels.join('; ') : '—'
    }, [
        form.assignees,
        getPanelKeyForTypeId,
        assigneeOptions,
        templateUsers,
        teams,
        roles,
        vendorCompanies,
        vendorContacts,
    ])

    const updateForm = patch => setForm(prev => ({ ...prev, ...patch }))

    const resetVendorFormModes = () => {
        setShowAddVendorCompanyForm(false)
        setShowAddVendorContactForm(false)
        setEditVendorCompany(null)
        setEditVendorContact(null)
        setConfirmDeleteVendorCompany(false)
        setConfirmDeleteVendorContact(false)
        setDeleteVendorCompanyWarning('')
    }

    const vendorCompanyEntry = useMemo(
        () =>
            (form.assignees || []).find(
                a => getPanelKeyForTypeId(a.typeId) === 'vendor-company',
            ),
        [form.assignees, getPanelKeyForTypeId],
    )

    const activeVendorCompanyId =
        vendorCompanyEntry?.assignedToId ||
        vendorContactEntry?.vendorCompanyId ||
        null

    const updateAssigneeEntry = (typeId, patch) => {
        setForm(prev => {
            const assignees = (prev.assignees || []).map(a =>
                Number(a.typeId) === Number(typeId) ? { ...a, ...patch } : a,
            )
            return { ...prev, ...syncLegacyAssigneeFields(assignees) }
        })
    }

    const toggleTriggerValue = text => {
        setForm(prev => {
            const current = prev.triggerValues || []
            const exists = isTriggerValueSelected(current, text)
            if (!exists && current.length >= MAX_TRIGGER_ANSWERS) {
                toastRef.current.warningToast(
                    `You can select up to ${MAX_TRIGGER_ANSWERS} trigger answers.`,
                )
                return prev
            }
            const triggerValues = exists
                ? current.filter(v => !isTriggerValueSelected([v], text))
                : [...current, text]
            return {
                ...prev,
                triggerValues,
                triggerValue: triggerValues[0] || '',
            }
        })
    }

    const selectAllTriggerValues = () => {
        const allTexts = answerOptions.map(opt => opt.text)
        const capped = allTexts.slice(0, MAX_TRIGGER_ANSWERS)
        setForm(prev => ({
            ...prev,
            triggerValues: capped,
            triggerValue: capped[0] || '',
        }))
        if (allTexts.length > MAX_TRIGGER_ANSWERS) {
            toastRef.current.warningToast(
                `Only the first ${MAX_TRIGGER_ANSWERS} answers were selected (max limit).`,
            )
        }
    }

    const toggleAssigneeType = typeId => {
        setForm(prev => {
            const current = prev.assignees || []
            const exists = current.some(
                a => Number(a.typeId) === Number(typeId),
            )
            let assignees
            if (exists) {
                if (current.length <= 1) return prev
                assignees = current.filter(
                    a => Number(a.typeId) !== Number(typeId),
                )
            } else {
                if (current.length >= MAX_ASSIGNEES) {
                    toastRef.current.warningToast(
                        `You can select up to ${MAX_ASSIGNEES} recipients.`,
                    )
                    return prev
                }
                assignees = [
                    ...current,
                    {
                        typeId,
                        assignedToId: null,
                        vendorCompanyId: null,
                    },
                ]
            }
            return { ...prev, ...syncLegacyAssigneeFields(assignees) }
        })
        resetVendorFormModes()
    }

    const getAssigneeEntry = typeId =>
        (form.assignees || []).find(
            a => Number(a.typeId) === Number(typeId),
        ) || { typeId, assignedToId: null, vendorCompanyId: null }

    const handleVendorCompanyDropdownChange = (typeId, field, value) => {
        if (value === ADD_VENDOR_COMPANY_KEY) {
            setEditVendorCompany(null)
            setConfirmDeleteVendorCompany(false)
            setDeleteVendorCompanyWarning('')
            setShowAddVendorCompanyForm(true)
            return
        }
        setShowAddVendorCompanyForm(false)
        setEditVendorCompany(null)
        setConfirmDeleteVendorCompany(false)
        setDeleteVendorCompanyWarning('')
        if (field === 'fixedAssignedToId') {
            updateAssigneeEntry(typeId, {
                assignedToId: value ? Number(value) : null,
            })
            return
        }
        updateAssigneeEntry(typeId, {
            vendorCompanyId: value ? Number(value) : null,
            assignedToId: null,
        })
        setShowAddVendorContactForm(false)
        setEditVendorContact(null)
        setConfirmDeleteVendorContact(false)
    }

    const handleVendorContactDropdownChange = (typeId, value) => {
        if (value === ADD_VENDOR_CONTACT_KEY) {
            const entry = getAssigneeEntry(typeId)
            if (!entry.vendorCompanyId) {
                toastRef.current.warningToast(
                    'Select a vendor company before adding a contact.',
                )
                return
            }
            setEditVendorContact(null)
            setConfirmDeleteVendorContact(false)
            setShowAddVendorContactForm(true)
            return
        }
        setShowAddVendorContactForm(false)
        setEditVendorContact(null)
        setConfirmDeleteVendorContact(false)
        updateAssigneeEntry(typeId, {
            assignedToId: value ? Number(value) : null,
        })
    }

    const handleVendorCompanyCreated = vendor => {
        if (!vendor?.id) return
        setVendorCompanies(prev => {
            const exists = prev.some(
                item => Number(item.id) === Number(vendor.id),
            )
            if (exists) {
                return prev.map(item =>
                    Number(item.id) === Number(vendor.id) ? vendor : item,
                )
            }
            return [...prev, vendor]
        })
        setShowAddVendorCompanyForm(false)
        const companyType = (form.assignees || []).find(
            a => getPanelKeyForTypeId(a.typeId) === 'vendor-company',
        )
        const contactType = (form.assignees || []).find(
            a => getPanelKeyForTypeId(a.typeId) === 'vendor-contact',
        )
        if (companyType) {
            updateAssigneeEntry(companyType.typeId, {
                assignedToId: Number(vendor.id),
            })
        } else if (contactType) {
            updateAssigneeEntry(contactType.typeId, {
                vendorCompanyId: Number(vendor.id),
                assignedToId: null,
            })
        }
        toastRef.current.successToast('Vendor company created.')
    }

    const handleVendorContactCreated = contact => {
        if (!contact?.id) return
        setVendorContacts(prev => {
            const exists = prev.some(
                item => Number(item.id) === Number(contact.id),
            )
            if (exists) {
                return prev.map(item =>
                    Number(item.id) === Number(contact.id) ? contact : item,
                )
            }
            return [...prev, contact]
        })
        setShowAddVendorContactForm(false)
        const contactType = (form.assignees || []).find(
            a => getPanelKeyForTypeId(a.typeId) === 'vendor-contact',
        )
        if (contactType) {
            updateAssigneeEntry(contactType.typeId, {
                assignedToId: Number(contact.id),
            })
        }
        toastRef.current.successToast('Vendor contact created.')
    }

    const handleVendorCompanyUpdated = vendor => {
        if (!vendor?.id) return
        setVendorCompanies(prev =>
            prev.map(item =>
                Number(item.id) === Number(vendor.id) ? vendor : item,
            ),
        )
        setEditVendorCompany(null)
        toastRef.current.successToast('Vendor company updated.')
    }

    const handleVendorContactUpdated = contact => {
        if (!contact?.id) return
        setVendorContacts(prev =>
            prev.map(item =>
                Number(item.id) === Number(contact.id) ? contact : item,
            ),
        )
        setEditVendorContact(null)
        toastRef.current.successToast('Vendor contact updated.')
    }

    const handleDeleteVendorCompany = async () => {
        const id = activeVendorCompanyId
        if (!id) return

        if (!confirmDeleteVendorCompany) {
            try {
                const res = await ActionPlanRuleService.getVendorContacts(id)
                const contacts = normalizeVendorContactList(res)
                if (contacts.length > 0) {
                    setDeleteVendorCompanyWarning(
                        `This vendor has ${contacts.length} contact(s). Deleting the company will not remove them.`,
                    )
                } else {
                    setDeleteVendorCompanyWarning('')
                }
            } catch {
                setDeleteVendorCompanyWarning('')
            }
            setConfirmDeleteVendorCompany(true)
            return
        }

        setDeletingVendorCompany(true)
        try {
            await ActionPlanRuleService.deleteVendorCompany(id)
            setVendorCompanies(prev =>
                prev.filter(v => Number(v.id) !== Number(id)),
            )
            const assigneeList = form.assignees || []
            assigneeList.forEach(a => {
                const panelKey = getPanelKeyForTypeId(a.typeId)
                if (
                    panelKey === 'vendor-company' &&
                    Number(a.assignedToId) === Number(id)
                ) {
                    updateAssigneeEntry(a.typeId, { assignedToId: null })
                }
                if (
                    panelKey === 'vendor-contact' &&
                    Number(a.vendorCompanyId) === Number(id)
                ) {
                    updateAssigneeEntry(a.typeId, {
                        vendorCompanyId: null,
                        assignedToId: null,
                    })
                    setVendorContacts([])
                }
            })
            setEditVendorCompany(null)
            toastRef.current.successToast('Vendor company removed.')
        } catch (err) {
            if (err?.status === 404) {
                setVendorCompanies(prev =>
                    prev.filter(v => Number(v.id) !== Number(id)),
                )
                toastRef.current.errorToast(
                    "This vendor isn't available anymore.",
                )
            } else {
                toastRef.current.errorToast(getApiErrorMessage(err))
            }
        } finally {
            setDeletingVendorCompany(false)
            setConfirmDeleteVendorCompany(false)
            setDeleteVendorCompanyWarning('')
        }
    }

    const handleDeleteVendorContact = async () => {
        const contactEntry = vendorContactEntry
        const id = contactEntry?.assignedToId
        if (!id) return

        if (!confirmDeleteVendorContact) {
            setConfirmDeleteVendorContact(true)
            return
        }

        setDeletingVendorContact(true)
        try {
            await ActionPlanRuleService.deleteVendorContact(id)
            setVendorContacts(prev =>
                prev.filter(c => Number(c.id) !== Number(id)),
            )
            if (contactEntry) {
                updateAssigneeEntry(contactEntry.typeId, {
                    assignedToId: null,
                })
            }
            setEditVendorContact(null)
            toastRef.current.successToast('Vendor contact removed.')
        } catch (err) {
            if (err?.status === 404) {
                setVendorContacts(prev =>
                    prev.filter(c => Number(c.id) !== Number(id)),
                )
                if (contactEntry) {
                    updateAssigneeEntry(contactEntry.typeId, {
                        assignedToId: null,
                    })
                }
                toastRef.current.errorToast(
                    "This vendor isn't available anymore.",
                )
            } else {
                toastRef.current.errorToast(getApiErrorMessage(err))
            }
        } finally {
            setDeletingVendorContact(false)
            setConfirmDeleteVendorContact(false)
        }
    }

    const selectPriority = p => {
        const level = getPriorityLevel(p.name)
        const sla =
            DEFAULT_PRIORITY_SLA[level] || DEFAULT_PRIORITY_SLA.medium
        updateForm({
            priorityId: p.id,
            ackSlaMinutes: clampSlaMinutes(sla.ack, level),
            resolveSlaMinutes: clampSlaMinutes(sla.resolve, level),
        })
    }

    const selectedPriorityLevel = getPriorityLevel(selectedPriority?.name)
    const slaMaxMinutes = getSlaMaxMinutesForPriority(selectedPriorityLevel)

    const updateSlaMinutes = (field, rawValue) => {
        const n = Number(rawValue)
        if (rawValue === '' || !Number.isFinite(n)) {
            updateForm({ [field]: rawValue })
            return
        }
        updateForm({
            [field]: clampSlaMinutes(n, selectedPriorityLevel),
        })
    }

    const isAssigneeEntryComplete = entry => {
        if (!entry) return false
        const panelKey = getPanelKeyForTypeId(entry.typeId)
        if (panelKey === 'vendor-contact') {
            return Boolean(entry.vendorCompanyId && entry.assignedToId)
        }
        return Boolean(entry.assignedToId)
    }

    const canNext = () => {
        if (step === 1) {
            return Boolean(form.auditQuestionId)
        }
        if (step === 2) {
            return (form.triggerValues || []).length > 0
        }
        if (step === 3) {
            if (form.customerMode !== 'selected') return true
            return (form.customerIds || []).length > 0
        }
        if (step === 4) {
            const list = form.assignees || []
            return list.length > 0 && list.every(isAssigneeEntryComplete)
        }
        if (step === 5) {
            return (
                form.priorityId &&
                form.categoryId &&
                Number(form.ackSlaMinutes) > 0 &&
                Number(form.resolveSlaMinutes) > 0
            )
        }
        return true
    }

    const handleSave = async () => {
        if (!numericTemplateId) {
            errorToast('Template ID not ready. Please reload the page.')
            return
        }
        setSaving(true)
        try {
            const level = getPriorityLevel(selectedPriority?.name)
            const saveForm = {
                ...form,
                ruleId: isEdit ? ruleId : null,
                ackSlaMinutes: clampSlaMinutes(form.ackSlaMinutes, level),
                resolveSlaMinutes: clampSlaMinutes(
                    form.resolveSlaMinutes,
                    level,
                ),
            }
            const payload = buildRulePayload(
                saveForm,
                numericTemplateId,
                isEdit,
            )
            if (Number.isFinite(Number(companyId)) && Number(companyId) > 0) {
                payload.CompanyId = Number(companyId)
            }
            const selectedPolicyId = form.escalationPolicyId
                ? Number(form.escalationPolicyId)
                : null
            let savedRuleId = isEdit ? Number(ruleId) : null

            if (!isEdit) {
                const created = await ActionPlanRuleService.create(payload)
                savedRuleId = Number(
                    created?.RuleId ??
                        created?.ruleId ??
                        created?.Data?.RuleId ??
                        created?.data?.RuleId ??
                        created?.Id ??
                        created?.id,
                )
            }

            // Link the policy before the final rule write. That follow-up call
            // can reload the rule without customers and clear CustomerIds.
            if (Number.isFinite(savedRuleId) && savedRuleId > 0) {
                try {
                    if (selectedPolicyId) {
                        await EscalationPolicyService.attachToRule(
                            savedRuleId,
                            selectedPolicyId,
                        )
                    } else if (isEdit) {
                        await EscalationPolicyService.unlinkFromRule(
                            savedRuleId,
                        )
                    }
                } catch {
                    // Rule body still carries EscalationPolicyId when supported.
                }
            }

            if (Number.isFinite(savedRuleId) && savedRuleId > 0) {
                payload.RuleId = savedRuleId
            }
            if (!isEdit) {
                payload.IsEnabled = saveForm.isEnabled !== false
            }
            if (isEdit) {
                await ActionPlanRuleService.update(ruleId, payload)
            } else if (Number.isFinite(savedRuleId) && savedRuleId > 0) {
                await ActionPlanRuleService.update(savedRuleId, payload)
            }

            if (payload.CustomerIds?.length) {
                const rulesRes =
                    await ActionPlanRuleService.getRulesByTemplate(
                        numericTemplateId,
                    )
                const rawList = Array.isArray(rulesRes)
                    ? rulesRes
                    : rulesRes?.Items ||
                      rulesRes?.Rules ||
                      (Array.isArray(rulesRes?.Data) ? rulesRes.Data : [])
                const rawSaved = rawList.find(rule => {
                    const id = rule?.RuleId ?? rule?.Id ?? rule?.id
                    return String(id) === String(savedRuleId)
                })
                const saved = rawSaved ? normalizeRule(rawSaved) : null
                const wanted = payload.CustomerIds.map(Number)
                const got = new Set((saved?.customerIds || []).map(Number))
                const rawText = JSON.stringify(rawSaved || {}).toLowerCase()
                const idsStored = wanted.every(id => got.has(id))
                const namesStored = selectedCustomerNames
                    .filter(Boolean)
                    .every(name =>
                        rawText.includes(String(name).trim().toLowerCase()),
                    )
                if (!idsStored && !namesStored) {
                    throw new Error(
                        'The rule saved, but the selected customers were not stored. Please save the customer step again.',
                    )
                }
            }

            successToast(isEdit ? 'Rule updated' : 'Rule saved')
            navigate(managePath)
        } catch (err) {
            const message = getApiErrorMessage(err)
            if (/customer/i.test(message)) {
                setCustomerSaveError(message)
                setStep(3)
            }
            errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <PageWrap>
                <LoaderBar content="Loading wizard…" />
            </PageWrap>
        )
    }

    return (
        <PageWrap>
            <PageContent>
                <Breadcrumb>
                    <a onClick={() => navigate('/app/templates')}>Templates</a>
                    {' › '}
                    <a onClick={() => navigate(managePath)}>{templateName}</a>
                    {' › '}
                    {isEdit ? 'Edit rule' : 'Add a new rule'}
                </Breadcrumb>
                <PageTitle>
                    {isEdit ? 'Edit auto-ticket rule' : 'Create an auto-ticket rule'}
                </PageTitle>
                <PageSubtitle>
                    6 quick steps. You can always come back and edit any of these
                    later.
                </PageSubtitle>

                <WizardStepper>
                    {STEP_LABELS.map((label, i) => {
                        const n = i + 1
                        return (
                            <StepPip
                                key={label}
                                $active={step === n}
                                $done={step > n}
                            >
                                <div className="num">{n}</div>
                                <div className="label">{label}</div>
                            </StepPip>
                        )
                    })}
                </WizardStepper>

                {step === 1 && (
                    <WizardPanel>
                        <h2>Step 1 — Which question should we watch?</h2>
                        <WhyBox>
                            A rule fires on one question. Pick the question whose
                            answer should trigger a ticket.
                        </WhyBox>
                        <Form>
                            <Form.Field required>
                                <label>Question</label>
                                <CustomDropdown
                                    fluid
                                    search={filterQuestionDropdownOptions}
                                    selection
                                    selectOnBlur={false}
                                    placeholder="— Select a question —"
                                    options={questionDropdownOptions}
                                    value={form.auditQuestionId ?? ''}
                                    onChange={(e, { value }) =>
                                        updateForm({
                                            auditQuestionId: value
                                                ? Number(value)
                                                : null,
                                            triggerValue: '',
                                            triggerValues: [],
                                        })
                                    }
                                    noResultsMessage="No matching questions"
                                />
                                <span className="helper">
                                    All questions from <b>{templateName}</b>.
                                </span>
                            </Form.Field>
                        </Form>
                        <WizardFooter>
                            <div className="left" />
                            <div className="right">
                                <AppSecondaryButton
                                    onClick={() => navigate(managePath)}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    onClick={() => setStep(2)}
                                    disabled={!canNext()}
                                >
                                    Next: Trigger answer →
                                </AppPrimaryButton>
                            </div>
                        </WizardFooter>
                    </WizardPanel>
                )}

                {step === 2 && (
                    <WizardPanel>
                        <h2>Step 2 — Which answer should trigger the ticket?</h2>
                        <WhyBox>
                            For <b>&ldquo;{selectedQuestion?.questionText}&rdquo;</b>
                            {answerOptions.length
                                ? ', pick one or more answers that should fire the rule.'
                                : ', this question has no selectable answers from the template.'}
                        </WhyBox>
                        <Form>
                            <Form.Field required>
                                <label>
                                    Trigger answer(s){' '}
                                    <span
                                        style={{
                                            fontWeight: 400,
                                            color: '#9ca3af',
                                        }}
                                    >
                                        (max {MAX_TRIGGER_ANSWERS})
                                    </span>
                                </label>
                                {answerOptions.length > 0 && (
                                    <div
                                        style={{
                                            marginBottom: 8,
                                            fontSize: 13,
                                        }}
                                    >
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            onClick={selectAllTriggerValues}
                                            onKeyDown={e =>
                                                e.key === 'Enter' &&
                                                selectAllTriggerValues()
                                            }
                                            style={{
                                                color: '#2563eb',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Select all
                                        </span>
                                        {' · '}
                                        {(form.triggerValues || []).length}{' '}
                                        selected
                                    </div>
                                )}
                                {!answerOptions.length ? (
                                    <Message
                                        warning
                                        content="This question does not have multiple-choice answers in the template. Choose a different question in Step 1, or add answer options to the template first."
                                    />
                                ) : (
                                    <RadioGroup>
                                        {answerOptions.map(opt => {
                                            const pres =
                                                getAnswerOptionPresentation(
                                                    opt.text,
                                                )
                                            const checked = isTriggerValueSelected(
                                                form.triggerValues,
                                                opt.text,
                                            )
                                            return (
                                                <RadioCard
                                                    key={opt.id ?? opt.text}
                                                    $checked={checked}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="trigger"
                                                        checked={checked}
                                                        onChange={() =>
                                                            toggleTriggerValue(
                                                                opt.text,
                                                            )
                                                        }
                                                    />
                                                    <div>
                                                        <div className="rtitle">
                                                            {pres.icon}{' '}
                                                            {pres.title}
                                                        </div>
                                                        <div className="rdesc">
                                                            {pres.desc}
                                                        </div>
                                                    </div>
                                                </RadioCard>
                                            )
                                        })}
                                    </RadioGroup>
                                )}
                            </Form.Field>
                        </Form>
                        <WizardFooter>
                            <div className="left">
                                <AppSecondaryButton onClick={() => setStep(1)}>
                                    ← Back
                                </AppSecondaryButton>
                            </div>
                            <div className="right">
                                <AppSecondaryButton
                                    onClick={() => navigate(managePath)}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    onClick={() => setStep(3)}
                                    disabled={!canNext()}
                                >
                                    Next: Customers →
                                </AppPrimaryButton>
                            </div>
                        </WizardFooter>
                    </WizardPanel>
                )}

                {step === 3 && (
                    <WizardPanel>
                        <h2>Step 3 — Which customers should this rule apply to?</h2>
                        <WhyBox>
                            Multiple customers are allowed. The default is{' '}
                            <b>All customers</b> — every inspection of this
                            template. Limit the rule when a ticket should be
                            created only for selected customers.
                        </WhyBox>
                        <Form>
                            <Form.Field required>
                                <label>Customer scope</label>
                                <RadioGroup>
                                    <RadioCard
                                        $checked={form.customerMode !== 'selected'}
                                    >
                                        <input
                                            type="radio"
                                            name="customerScope"
                                            checked={
                                                form.customerMode !== 'selected'
                                            }
                                            onChange={() =>
                                                setCustomerMode('all')
                                            }
                                        />
                                        <div>
                                            <div className="rtitle">
                                                All customers
                                            </div>
                                            <div className="rdesc">
                                                Every inspection of this template
                                            </div>
                                        </div>
                                    </RadioCard>
                                    <RadioCard
                                        $checked={
                                            form.customerMode === 'selected'
                                        }
                                    >
                                        <input
                                            type="radio"
                                            name="customerScope"
                                            checked={
                                                form.customerMode === 'selected'
                                            }
                                            onChange={() =>
                                                setCustomerMode('selected')
                                            }
                                        />
                                        <div>
                                            <div className="rtitle">
                                                Only selected customers
                                            </div>
                                            <div className="rdesc">
                                                Ticket only for these
                                                customers&apos; inspections
                                            </div>
                                        </div>
                                    </RadioCard>
                                </RadioGroup>
                            </Form.Field>
                            {form.customerMode === 'selected' && (
                                <>
                                    <CustomerSearch
                                        type="text"
                                        placeholder="Search customers..."
                                        value={customerSearch}
                                        onChange={e =>
                                            setCustomerSearch(e.target.value)
                                        }
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                            }
                                        }}
                                    />
                                    <CustomerScopeList>
                                        <CustomerScopeToolbar>
                                            <span>{customerCountLabel}</span>
                                            {filteredCustomers.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={
                                                        toggleVisibleCustomers
                                                    }
                                                >
                                                    {allVisibleCustomersSelected
                                                        ? 'Clear'
                                                        : 'Select all'}
                                                </button>
                                            )}
                                        </CustomerScopeToolbar>
                                        <CustomerScopeRows>
                                            {!filteredCustomers.length ? (
                                                <div
                                                    style={{
                                                        padding: '14px 12px',
                                                        color: '#6b7280',
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {customerEmptyMessage}
                                                </div>
                                            ) : (
                                                filteredCustomers.map(c => {
                                                    const checked = (
                                                        form.customerIds || []
                                                    ).some(
                                                        id =>
                                                            Number(id) === c.id,
                                                    )
                                                    return (
                                                        <CustomerScopeRow
                                                            key={c.id}
                                                            $checked={checked}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() =>
                                                                    toggleCustomerId(
                                                                        c.id,
                                                                    )
                                                                }
                                                            />
                                                            <span className="name">
                                                                {c.name}
                                                            </span>
                                                            <span className="id">
                                                                {c.id}
                                                            </span>
                                                        </CustomerScopeRow>
                                                    )
                                                })
                                            )}
                                        </CustomerScopeRows>
                                    </CustomerScopeList>
                                    {(form.customerIds || []).length > 0 && (
                                        <CustomerChipRow>
                                            {(form.customerIds || []).map(id => {
                                                const n = Number(id)
                                                const name =
                                                    customerNameById[n] ||
                                                    `Customer #${n}`
                                                return (
                                                    <CustomerChip key={n}>
                                                        {name}
                                                        <button
                                                            type="button"
                                                            aria-label={`Remove ${name}`}
                                                            onClick={() =>
                                                                toggleCustomerId(
                                                                    n,
                                                                )
                                                            }
                                                        >
                                                            ×
                                                        </button>
                                                    </CustomerChip>
                                                )
                                            })}
                                            <CustomerSelectedCount>
                                                {(form.customerIds || []).length}{' '}
                                                selected
                                            </CustomerSelectedCount>
                                        </CustomerChipRow>
                                    )}
                                    {form.customerMode === 'selected' &&
                                        !(form.customerIds || []).length && (
                                            <span
                                                style={{
                                                    display: 'block',
                                                    marginTop: 8,
                                                    color: '#6b7280',
                                                    fontSize: 12,
                                                }}
                                            >
                                                Pick at least one customer, or
                                                choose All customers.
                                            </span>
                                        )}
                                </>
                            )}
                            {customerSaveError && (
                                <Message
                                    negative
                                    style={{
                                        maxWidth: 640,
                                        marginTop: 12,
                                    }}
                                >
                                    {customerSaveError}
                                </Message>
                            )}
                        </Form>
                        <WizardFooter>
                            <div className="left">
                                <AppSecondaryButton onClick={() => setStep(2)}>
                                    ← Back
                                </AppSecondaryButton>
                            </div>
                            <div className="right">
                                <AppSecondaryButton
                                    onClick={() => navigate(managePath)}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    onClick={() => setStep(4)}
                                    disabled={!canNext()}
                                >
                                    Next: Who gets the ticket
                                </AppPrimaryButton>
                            </div>
                        </WizardFooter>
                    </WizardPanel>
                )}

                {step === 4 && (
                    <WizardPanel>
                        <h2>Step 4 — Who should get the ticket?</h2>
                        <WhyBox>
                            Pick one or more recipients. Options come from your
                            company&apos;s assignee types (user, team, or
                            vendor).
                        </WhyBox>
                        <Form>
                            <Form.Field required>
                                <label>
                                    Send the ticket to…{' '}
                                    <span
                                        style={{
                                            fontWeight: 400,
                                            color: '#9ca3af',
                                        }}
                                    >
                                        (max {MAX_ASSIGNEES})
                                    </span>
                                </label>
                                <span className="helper">
                                    {(form.assignees || []).length} recipient
                                    type
                                    {(form.assignees || []).length === 1
                                        ? ''
                                        : 's'}{' '}
                                    selected
                                </span>
                                <RadioGroup>
                                    {assigneeOptions.map(opt => {
                                        const checked = (
                                            form.assignees || []
                                        ).some(
                                            a =>
                                                Number(a.typeId) ===
                                                Number(opt.typeId),
                                        )
                                        return (
                                            <RadioCard
                                                key={opt.typeId}
                                                $checked={checked}
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="assigneeType"
                                                    checked={checked}
                                                    onChange={() =>
                                                        toggleAssigneeType(
                                                            opt.typeId,
                                                        )
                                                    }
                                                />
                                                <div>
                                                    <div className="rtitle">
                                                        {opt.title}
                                                    </div>
                                                    <div className="rdesc">
                                                        {opt.desc}
                                                    </div>
                                                </div>
                                            </RadioCard>
                                        )
                                    })}
                                </RadioGroup>
                            </Form.Field>

                            {(form.assignees || []).map(entry => {
                                const opt = assigneeOptions.find(
                                    o =>
                                        Number(o.typeId) ===
                                        Number(entry.typeId),
                                )
                                const panelKey =
                                    opt?.panelKey ||
                                    getPanelKeyForTypeId(entry.typeId)
                                const { typeId } = entry
                                const selectedUserForType = templateUsers.find(
                                    u =>
                                        Number(u.id) ===
                                        Number(entry.assignedToId),
                                )
                                const selectedVendorForType =
                                    vendorCompanies.find(
                                        c =>
                                            Number(c.id) ===
                                            Number(entry.assignedToId),
                                    )
                                const selectedContactForType =
                                    vendorContacts.find(
                                        c =>
                                            Number(c.id) ===
                                            Number(entry.assignedToId),
                                    )
                                const companyForContact = vendorCompanies.find(
                                    c =>
                                        Number(c.id) ===
                                        Number(entry.vendorCompanyId),
                                )

                                return (
                                    <React.Fragment key={typeId}>
                                        {panelKey === 'user' && (
                                            <Form.Field required>
                                                <label>
                                                    Choose the person
                                                    {opt?.title
                                                        ? ` (${opt.title})`
                                                        : ''}
                                                </label>
                                                <span className="helper">
                                                    Showing internal users with
                                                    active eAuditor accounts in
                                                    your company.
                                                </span>
                                                <CustomDropdown
                                                    fluid
                                                    search
                                                    selection
                                                    selectOnBlur={false}
                                                    placeholder="Select user"
                                                    options={
                                                        userDropdownOptions
                                                    }
                                                    value={
                                                        entry.assignedToId ?? ''
                                                    }
                                                    onChange={(e, { value }) =>
                                                        updateAssigneeEntry(
                                                            typeId,
                                                            {
                                                                assignedToId:
                                                                    value
                                                                        ? Number(
                                                                              value,
                                                                          )
                                                                        : null,
                                                            },
                                                        )
                                                    }
                                                    disabled={
                                                        !userDropdownOptions.length
                                                    }
                                                    noResultsMessage="No users found"
                                                />
                                                {!userDropdownOptions.length && (
                                                    <Message
                                                        info
                                                        size="small"
                                                        content="No users found for this template. Assign users on the template or add team members in Settings."
                                                    />
                                                )}
                                                {selectedUserForType?.email && (
                                                    <Message
                                                        info
                                                        size="small"
                                                        content={
                                                            <>
                                                                📧 Notification
                                                                email:{' '}
                                                                <b>
                                                                    {
                                                                        selectedUserForType.email
                                                                    }
                                                                </b>
                                                                <br />
                                                                📱 Also sends
                                                                in-app
                                                                notification
                                                                (and SMS if
                                                                priority is
                                                                Critical)
                                                            </>
                                                        }
                                                    />
                                                )}
                                            </Form.Field>
                                        )}

                                        {panelKey === 'role' && (
                                            <Form.Field required>
                                                <label>
                                                    Choose the role
                                                    {opt?.title
                                                        ? ` (${opt.title})`
                                                        : ''}
                                                </label>
                                                <CustomDropdown
                                                    fluid
                                                    search
                                                    selection
                                                    selectOnBlur={false}
                                                    placeholder="Select role"
                                                    options={
                                                        roleDropdownOptions
                                                    }
                                                    value={
                                                        entry.assignedToId ?? ''
                                                    }
                                                    onChange={(e, { value }) =>
                                                        updateAssigneeEntry(
                                                            typeId,
                                                            {
                                                                assignedToId:
                                                                    value
                                                                        ? Number(
                                                                              value,
                                                                          )
                                                                        : null,
                                                            },
                                                        )
                                                    }
                                                    disabled={
                                                        !roleDropdownOptions.length
                                                    }
                                                    noResultsMessage="No roles found"
                                                />
                                            </Form.Field>
                                        )}

                                        {panelKey === 'team' && (
                                            <Form.Field required>
                                                <label>
                                                    Choose the team
                                                    {opt?.title
                                                        ? ` (${opt.title})`
                                                        : ''}
                                                </label>
                                                <span className="helper">
                                                    Loaded from Action Plan
                                                    teams, or your Team Groups
                                                    if that list is empty.
                                                </span>
                                                <CustomDropdown
                                                    fluid
                                                    search
                                                    selection
                                                    selectOnBlur={false}
                                                    placeholder="Select team"
                                                    options={
                                                        teamDropdownOptions
                                                    }
                                                    value={
                                                        entry.assignedToId ?? ''
                                                    }
                                                    onChange={(e, { value }) =>
                                                        updateAssigneeEntry(
                                                            typeId,
                                                            {
                                                                assignedToId:
                                                                    value
                                                                        ? Number(
                                                                              value,
                                                                          )
                                                                        : null,
                                                            },
                                                        )
                                                    }
                                                    disabled={
                                                        !teamDropdownOptions.length
                                                    }
                                                    noResultsMessage="No teams found"
                                                />
                                                {!teamDropdownOptions.length && (
                                                    <Message
                                                        warning
                                                        size="small"
                                                        content="No teams are configured yet. Create a Team Group in Settings or ask your admin to enable Action Plan teams."
                                                    />
                                                )}
                                            </Form.Field>
                                        )}

                                        {panelKey === 'vendor-company' && (
                                            <Form.Field required>
                                                <label>
                                                    Choose the vendor company
                                                    {opt?.title
                                                        ? ` (${opt.title})`
                                                        : ''}
                                                </label>
                                                <VendorDropdownStyles>
                                                    <CustomDropdown
                                                        fluid
                                                        search
                                                        selection
                                                        selectOnBlur={false}
                                                        placeholder="Select vendor company"
                                                        options={
                                                            vendorCompanyDropdownOptions
                                                        }
                                                        value={
                                                            entry.assignedToId ??
                                                            ''
                                                        }
                                                        onChange={(
                                                            e,
                                                            { value },
                                                        ) =>
                                                            handleVendorCompanyDropdownChange(
                                                                typeId,
                                                                'fixedAssignedToId',
                                                                value,
                                                            )
                                                        }
                                                        noResultsMessage="No vendor companies found"
                                                    />
                                                </VendorDropdownStyles>
                                                {showAddVendorCompanyForm && (
                                                    <AddVendorCompanyForm
                                                        onCancel={() =>
                                                            setShowAddVendorCompanyForm(
                                                                false,
                                                            )
                                                        }
                                                        onCreated={
                                                            handleVendorCompanyCreated
                                                        }
                                                        onServerError={msg =>
                                                            toastRef.current.errorToast(
                                                                msg,
                                                            )
                                                        }
                                                    />
                                                )}
                                                {editVendorCompany && (
                                                    <EditVendorCompanyForm
                                                        vendor={
                                                            editVendorCompany
                                                        }
                                                        onCancel={() =>
                                                            setEditVendorCompany(
                                                                null,
                                                            )
                                                        }
                                                        onUpdated={
                                                            handleVendorCompanyUpdated
                                                        }
                                                        onServerError={msg =>
                                                            toastRef.current.errorToast(
                                                                msg,
                                                            )
                                                        }
                                                    />
                                                )}
                                                {selectedVendorForType &&
                                                    !showAddVendorCompanyForm &&
                                                    !editVendorCompany && (
                                                        <VendorManageBar
                                                            label={
                                                                selectedVendorForType.name
                                                            }
                                                            onEdit={() => {
                                                                setEditVendorCompany(
                                                                    selectedVendorForType,
                                                                )
                                                                setShowAddVendorCompanyForm(
                                                                    false,
                                                                )
                                                                setConfirmDeleteVendorCompany(
                                                                    false,
                                                                )
                                                            }}
                                                            onDelete={
                                                                handleDeleteVendorCompany
                                                            }
                                                            deleting={
                                                                deletingVendorCompany
                                                            }
                                                            confirmDelete={
                                                                confirmDeleteVendorCompany
                                                            }
                                                            deleteWarning={
                                                                deleteVendorCompanyWarning
                                                            }
                                                        />
                                                    )}
                                                {!vendorCompanies.length &&
                                                    !showAddVendorCompanyForm && (
                                                        <Message
                                                            info
                                                            size="small"
                                                            content="No vendor companies yet. Use “+ Add new vendor company…” in the dropdown to create one."
                                                        />
                                                    )}
                                            </Form.Field>
                                        )}

                                        {panelKey === 'vendor-contact' && (
                                            <>
                                                <Form.Field required>
                                                    <label>
                                                        Vendor company
                                                        {opt?.title
                                                            ? ` (${opt.title})`
                                                            : ''}
                                                    </label>
                                                    <VendorDropdownStyles>
                                                        <CustomDropdown
                                                            fluid
                                                            search
                                                            selection
                                                            selectOnBlur={false}
                                                            placeholder="Select vendor company"
                                                            options={
                                                                vendorCompanyDropdownOptions
                                                            }
                                                            value={
                                                                entry.vendorCompanyId ??
                                                                ''
                                                            }
                                                            onChange={(
                                                                e,
                                                                { value },
                                                            ) =>
                                                                handleVendorCompanyDropdownChange(
                                                                    typeId,
                                                                    'vendorCompanyId',
                                                                    value,
                                                                )
                                                            }
                                                            noResultsMessage="No vendor companies found"
                                                        />
                                                    </VendorDropdownStyles>
                                                    {showAddVendorCompanyForm && (
                                                        <AddVendorCompanyForm
                                                            onCancel={() =>
                                                                setShowAddVendorCompanyForm(
                                                                    false,
                                                                )
                                                            }
                                                            onCreated={
                                                                handleVendorCompanyCreated
                                                            }
                                                            onServerError={msg =>
                                                                toastRef.current.errorToast(
                                                                    msg,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {editVendorCompany && (
                                                        <EditVendorCompanyForm
                                                            vendor={
                                                                editVendorCompany
                                                            }
                                                            onCancel={() =>
                                                                setEditVendorCompany(
                                                                    null,
                                                                )
                                                            }
                                                            onUpdated={
                                                                handleVendorCompanyUpdated
                                                            }
                                                            onServerError={msg =>
                                                                toastRef.current.errorToast(
                                                                    msg,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {companyForContact &&
                                                        !showAddVendorCompanyForm &&
                                                        !editVendorCompany && (
                                                            <VendorManageBar
                                                                label={
                                                                    companyForContact.name
                                                                }
                                                                onEdit={() => {
                                                                    setEditVendorCompany(
                                                                        companyForContact,
                                                                    )
                                                                    setShowAddVendorCompanyForm(
                                                                        false,
                                                                    )
                                                                    setConfirmDeleteVendorCompany(
                                                                        false,
                                                                    )
                                                                }}
                                                                onDelete={
                                                                    handleDeleteVendorCompany
                                                                }
                                                                deleting={
                                                                    deletingVendorCompany
                                                                }
                                                                confirmDelete={
                                                                    confirmDeleteVendorCompany
                                                                }
                                                                deleteWarning={
                                                                    deleteVendorCompanyWarning
                                                                }
                                                            />
                                                        )}
                                                    {!vendorCompanies.length &&
                                                        !showAddVendorCompanyForm && (
                                                            <Message
                                                                info
                                                                size="small"
                                                                content="No vendor companies yet. Use “+ Add new vendor company…” in the dropdown to create one."
                                                            />
                                                        )}
                                                </Form.Field>
                                                <Form.Field required>
                                                    <label>
                                                        Choose the contact
                                                    </label>
                                                    <VendorDropdownStyles>
                                                        <CustomDropdown
                                                            fluid
                                                            search
                                                            selection
                                                            selectOnBlur={false}
                                                            placeholder="Select vendor contact"
                                                            options={
                                                                vendorContactDropdownOptions
                                                            }
                                                            value={
                                                                entry.assignedToId ??
                                                                ''
                                                            }
                                                            onChange={(
                                                                e,
                                                                { value },
                                                            ) =>
                                                                handleVendorContactDropdownChange(
                                                                    typeId,
                                                                    value,
                                                                )
                                                            }
                                                            disabled={
                                                                !entry.vendorCompanyId
                                                            }
                                                            noResultsMessage="No contacts found"
                                                        />
                                                    </VendorDropdownStyles>
                                                    {showAddVendorContactForm &&
                                                        entry.vendorCompanyId && (
                                                            <AddVendorContactForm
                                                                vendorCompanyId={
                                                                    entry.vendorCompanyId
                                                                }
                                                                onCancel={() =>
                                                                    setShowAddVendorContactForm(
                                                                        false,
                                                                    )
                                                                }
                                                                onCreated={
                                                                    handleVendorContactCreated
                                                                }
                                                                onServerError={msg =>
                                                                    toastRef.current.errorToast(
                                                                        msg,
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    {editVendorContact && (
                                                        <EditVendorContactForm
                                                            contact={
                                                                editVendorContact
                                                            }
                                                            onCancel={() =>
                                                                setEditVendorContact(
                                                                    null,
                                                                )
                                                            }
                                                            onUpdated={
                                                                handleVendorContactUpdated
                                                            }
                                                            onServerError={msg =>
                                                                toastRef.current.errorToast(
                                                                    msg,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                    {selectedContactForType &&
                                                        !showAddVendorContactForm &&
                                                        !editVendorContact && (
                                                            <VendorManageBar
                                                                label={
                                                                    selectedContactForType.name
                                                                }
                                                                onEdit={() => {
                                                                    setEditVendorContact(
                                                                        selectedContactForType,
                                                                    )
                                                                    setShowAddVendorContactForm(
                                                                        false,
                                                                    )
                                                                    setConfirmDeleteVendorContact(
                                                                        false,
                                                                    )
                                                                }}
                                                                onDelete={
                                                                    handleDeleteVendorContact
                                                                }
                                                                deleting={
                                                                    deletingVendorContact
                                                                }
                                                                confirmDelete={
                                                                    confirmDeleteVendorContact
                                                                }
                                                            />
                                                        )}
                                                    {entry.vendorCompanyId &&
                                                        !vendorContacts.length &&
                                                        !showAddVendorContactForm && (
                                                            <Message
                                                                info
                                                                size="small"
                                                                content="No contacts for this vendor yet. Use “+ Add new vendor contact…” in the dropdown to create one."
                                                            />
                                                        )}
                                                </Form.Field>
                                            </>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                        </Form>

                        <WizardFooter>
                            <div className="left">
                                <AppSecondaryButton onClick={() => setStep(3)}>
                                    ← Back
                                </AppSecondaryButton>
                            </div>
                            <div className="right">
                                <AppSecondaryButton
                                    onClick={() => navigate(managePath)}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    onClick={() => setStep(5)}
                                    disabled={!canNext()}
                                >
                                    Next: Urgency →
                                </AppPrimaryButton>
                            </div>
                        </WizardFooter>
                    </WizardPanel>
                )}

                {step === 5 && (
                    <WizardPanel>
                        <h2>Step 5 — How urgent is this?</h2>
                        <WhyBox>
                            Pick a level. We&apos;ll suggest deadlines based on
                            it, but you can override the exact minutes below.
                            Critical allows a maximum of 1 hour (60 minutes).
                        </WhyBox>
                        <Form>
                            <Form.Field required>
                                <label>Priority</label>
                                <UrgencyGrid>
                                    {priorities.map(p => {
                                        const level = getPriorityLevel(p.name)
                                        const sla =
                                            DEFAULT_PRIORITY_SLA[level] ||
                                            DEFAULT_PRIORITY_SLA.medium
                                        return (
                                            <UrgCard
                                                key={p.id}
                                                $selected={
                                                    form.priorityId === p.id
                                                }
                                                onClick={() => selectPriority(p)}
                                            >
                                                <div className="icon">
                                                    {URGENCY_ICONS[level] || '●'}
                                                </div>
                                                <div className="name">{p.name}</div>
                                                <div className="sla">
                                                    Ack:{' '}
                                                    {formatMinutesLabel(sla.ack)}{' '}
                                                    · Resolve:{' '}
                                                    {formatMinutesLabel(
                                                        sla.resolve,
                                                    )}
                                                </div>
                                            </UrgCard>
                                        )
                                    })}
                                </UrgencyGrid>
                            </Form.Field>
                            <Form.Group widths="equal">
                                <Form.Field required>
                                    <label>Acknowledge within (minutes)</label>
                                    <span className="helper">
                                        How long the assignee has to click
                                        &ldquo;Got it.&rdquo;
                                        {slaMaxMinutes
                                            ? ` Critical max: ${slaMaxMinutes} min.`
                                            : ''}
                                    </span>
                                    <Form.Input
                                        type="number"
                                        min={1}
                                        max={slaMaxMinutes || undefined}
                                        value={form.ackSlaMinutes}
                                        onChange={(e, { value }) =>
                                            updateSlaMinutes(
                                                'ackSlaMinutes',
                                                value,
                                            )
                                        }
                                    />
                                </Form.Field>
                                <Form.Field required>
                                    <label>Resolve within (minutes)</label>
                                    <span className="helper">
                                        How long they have to fix &amp; close
                                        the ticket.
                                        {slaMaxMinutes
                                            ? ` Critical max: ${slaMaxMinutes} min.`
                                            : ''}
                                    </span>
                                    <Form.Input
                                        type="number"
                                        min={1}
                                        max={slaMaxMinutes || undefined}
                                        value={form.resolveSlaMinutes}
                                        onChange={(e, { value }) =>
                                            updateSlaMinutes(
                                                'resolveSlaMinutes',
                                                value,
                                            )
                                        }
                                    />
                                </Form.Field>
                            </Form.Group>
                            <PolicyFieldBox>
                                <Form.Field>
                                    <label>
                                        Escalation policy{' '}
                                        <span
                                            style={{
                                                fontWeight: 400,
                                                color: '#6b7280',
                                                fontSize: 12,
                                            }}
                                        >
                                            (optional)
                                        </span>
                                    </label>
                                    <span className="helper">
                                        Attach a reusable escalation policy to
                                        this rule. If left blank, tickets get
                                        the initial email + push but no
                                        reminder / escalation / overdue
                                        behaviour.
                                    </span>
                                    <CustomDropdown
                                        fluid
                                        search
                                        selection
                                        selectOnBlur={false}
                                        placeholder="— None (no escalation) —"
                                        options={policyDropdownOptions}
                                        value={form.escalationPolicyId ?? ''}
                                        onChange={(_, { value }) => {
                                            if (value === CREATE_NEW_POLICY_KEY) {
                                                setEditingPolicy(null)
                                                setPolicyModalOpen(true)
                                                return
                                            }
                                            updateForm({
                                                escalationPolicyId: value
                                                    ? Number(value)
                                                    : null,
                                            })
                                        }}
                                    />
                                    {selectedPolicy && (
                                        <PolicyHint>
                                            {formatPolicyHint(selectedPolicy)}{' '}
                                            <LinkBtn
                                                type="button"
                                                onClick={() => {
                                                    setEditingPolicy(
                                                        selectedPolicy,
                                                    )
                                                    setPolicyModalOpen(true)
                                                }}
                                            >
                                                Edit policy →
                                            </LinkBtn>
                                        </PolicyHint>
                                    )}
                                </Form.Field>
                            </PolicyFieldBox>
                            <Form.Field required>
                                <label>
                                    Category{' '}
                                    <span
                                        style={{
                                            fontWeight: 400,
                                            color: '#9ca3af',
                                        }}
                                    >
                                        (for reporting)
                                    </span>
                                </label>
                                <CustomDropdown
                                    fluid
                                    search
                                    selection
                                    selectOnBlur={false}
                                    placeholder="Select category"
                                    options={categoryDropdownOptions}
                                    value={form.categoryId ?? ''}
                                    onChange={(e, { value }) => {
                                        if (value === CREATE_NEW_CATEGORY_KEY) {
                                            setEditingCategory(null)
                                            setCategoryModalOpen(true)
                                            return
                                        }
                                        const cat = categories.find(
                                            c =>
                                                Number(c.id) === Number(value),
                                        )
                                        updateForm({
                                            categoryId: value
                                                ? Number(value)
                                                : null,
                                            ticketType:
                                                cat?.name || form.ticketType,
                                        })
                                    }}
                                    disabled={
                                        !categoryDropdownOptions.length &&
                                        !canManageCategories
                                    }
                                    noResultsMessage="No categories found"
                                />
                                {selectedCategory && (
                                    <PolicyHint>
                                        Currently selected:{' '}
                                        <b>{selectedCategory.name}</b>
                                        {canManageCategories && (
                                            <>
                                                {' '}
                                                <LinkBtn
                                                    type="button"
                                                    onClick={() => {
                                                        setEditingCategory(
                                                            selectedCategory,
                                                        )
                                                        setCategoryModalOpen(
                                                            true,
                                                        )
                                                    }}
                                                >
                                                    Edit category →
                                                </LinkBtn>
                                            </>
                                        )}
                                    </PolicyHint>
                                )}
                                {canManageCategories && (
                                    <PolicyHint style={{ marginTop: 6 }}>
                                        <LinkBtn
                                            type="button"
                                            onClick={() =>
                                                navigate(
                                                    `/app/settings/${SettingsTabs.TicketCategories}`,
                                                )
                                            }
                                        >
                                            Manage all categories →
                                        </LinkBtn>
                                    </PolicyHint>
                                )}
                                {!categoryDropdownOptions.filter(
                                    o => o.value !== CREATE_NEW_CATEGORY_KEY,
                                ).length && (
                                    <Message
                                        warning
                                        size="small"
                                        content={
                                            canManageCategories
                                                ? 'No categories yet. Use “Add new category…” above or Manage all categories.'
                                                : 'No categories are configured for your company yet. Contact your administrator.'
                                        }
                                    />
                                )}
                            </Form.Field>
                            <Form.Field>
                                <label>Ticket type label</label>
                                <Form.Input
                                    value={form.ticketType}
                                    placeholder="e.g. Safety, Quality"
                                    onChange={(e, { value }) =>
                                        updateForm({ ticketType: value })
                                    }
                                />
                            </Form.Field>
                        </Form>
                        <WizardFooter>
                            <div className="left">
                                <AppSecondaryButton onClick={() => setStep(4)}>
                                    ← Back
                                </AppSecondaryButton>
                            </div>
                            <div className="right">
                                <AppSecondaryButton
                                    onClick={() => navigate(managePath)}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    onClick={() => setStep(6)}
                                    disabled={!canNext()}
                                >
                                    Next: Review →
                                </AppPrimaryButton>
                            </div>
                        </WizardFooter>
                    </WizardPanel>
                )}

                {step === 6 && (
                    <WizardPanel>
                        <h2>Step 6 — Review &amp; save</h2>
                        <WhyBox>
                            Here&apos;s what&apos;ll happen when an auditor picks
                            a trigger answer. Click <b>Save rule</b> if it looks
                            right.
                        </WhyBox>
                        <PreviewCard>
                            <h3>✓ Your {isEdit ? 'updated' : 'new'} rule</h3>
                            <dl>
                                <dt>On template</dt>
                                <dd>{templateName}</dd>
                                <dt>When the question</dt>
                                <dd>
                                    &ldquo;{selectedQuestion?.questionText}&rdquo;
                                </dd>
                                <dt>Is answered</dt>
                                <dd>
                                    <b>
                                        {(form.triggerValues || []).length
                                            ? (form.triggerValues || []).join(
                                                  ', ',
                                              )
                                            : form.triggerValue || '—'}
                                    </b>
                                </dd>
                                <dt>Customers</dt>
                                <dd>
                                    <PillCustomers
                                        $all={form.customerMode !== 'selected'}
                                    >
                                        {customerSummaryLabel}
                                    </PillCustomers>
                                </dd>
                                <dt>Create a ticket for</dt>
                                <dd>{assigneeLabel}</dd>
                                <dt>Priority</dt>
                                <dd
                                    style={{
                                        color:
                                            getPriorityLevel(
                                                selectedPriority?.name,
                                            ) === 'critical'
                                                ? '#dc2626'
                                                : undefined,
                                        fontWeight: 700,
                                    }}
                                >
                                    {selectedPriority?.name || '—'}
                                </dd>
                                <dt>Must acknowledge in</dt>
                                <dd>
                                    {formatMinutesLabel(form.ackSlaMinutes)}
                                </dd>
                                <dt>Must resolve in</dt>
                                <dd>
                                    {formatMinutesLabel(form.resolveSlaMinutes)}
                                </dd>
                                <dt>Escalation policy</dt>
                                <dd>
                                    {selectedPolicy
                                        ? selectedPolicy.name
                                        : 'None (no escalation)'}
                                </dd>
                                {selectedCategory && (
                                    <>
                                        <dt>Category</dt>
                                        <dd>{selectedCategory.name}</dd>
                                    </>
                                )}
                                {form.ticketType && (
                                    <>
                                        <dt>Ticket type</dt>
                                        <dd>{form.ticketType}</dd>
                                    </>
                                )}
                            </dl>
                            <h3 style={{ marginTop: 18 }}>Ticket preview</h3>
                            <div className="ttl">
                                {form.ticketType || 'General'}:{' '}
                                {selectedQuestion?.questionText} — Audit
                                #{'{auditId}'}
                            </div>
                        </PreviewCard>
                        <WizardFooter>
                            <div className="left">
                                <AppSecondaryButton onClick={() => setStep(5)}>
                                    ← Back
                                </AppSecondaryButton>
                            </div>
                            <div className="right">
                                <AppSecondaryButton
                                    onClick={() => navigate(managePath)}
                                >
                                    Cancel
                                </AppSecondaryButton>
                                <AppPrimaryButton
                                    onClick={handleSave}
                                    loading={saving}
                                    disabled={saving}
                                >
                                    ✓ Save rule
                                </AppPrimaryButton>
                            </div>
                        </WizardFooter>
                    </WizardPanel>
                )}
            </PageContent>
            <EscalationPolicyFormModal
                open={policyModalOpen}
                policy={editingPolicy}
                onClose={() => {
                    setPolicyModalOpen(false)
                    setEditingPolicy(null)
                }}
                onSaved={saved => {
                    if (!saved?.id) return
                    setEscalationPolicies(prev => {
                        const without = prev.filter(p => p.id !== saved.id)
                        return [saved, ...without]
                    })
                    updateForm({ escalationPolicyId: saved.id })
                }}
            />
            <ActionPlanCategoryFormModal
                open={categoryModalOpen}
                category={editingCategory}
                onClose={() => {
                    setCategoryModalOpen(false)
                    setEditingCategory(null)
                }}
                onSaved={saved => {
                    if (!saved?.id) return
                    if (saved.softDeleted || saved.isActive === false) {
                        setCategories(prev =>
                            prev.filter(c => c.id !== saved.id),
                        )
                        if (Number(form.categoryId) === Number(saved.id)) {
                            updateForm({
                                categoryId: null,
                                ticketType: '',
                            })
                        }
                        return
                    }
                    setCategories(prev => {
                        const without = prev.filter(c => c.id !== saved.id)
                        return [
                            { id: saved.id, name: saved.name },
                            ...without,
                        ]
                    })
                    updateForm({
                        categoryId: saved.id,
                        ticketType: saved.name || form.ticketType,
                    })
                }}
            />
        </PageWrap>
    )
}
